// Copyright (C) 2019-2022 Intel Corporation
// Copyright (C) 2022-2023 CVAT.ai Corporation
//
// SPDX-License-Identifier = MIT

export enum ShareFileType {
    DIR = 'DIR',
    REG = 'REG',
}

export enum ChunkType {
    IMAGESET = 'imageset',
    VIDEO = 'video',
}

export enum InstanceType {
    PROJECT = 'project',
    TASK = 'task',
    JOB = 'job',
    OBJECT = 'object',
    LABEL = 'label',
    ATTRIBUTE = 'attribute',
}

const InstanceTypeChineseMapping: Record<string, string> = {
    [InstanceType.PROJECT.toString()]: '项目',
    [InstanceType.TASK.toString()]: '任务',
    [InstanceType.JOB.toString()]: '作业',
    [InstanceType.OBJECT.toString()]: '标签',
    [InstanceType.ATTRIBUTE.toString()]: '属性',
};

export const GetInstanceTypeChinese = (type: string): string => InstanceTypeChineseMapping[type] || '未知';

export enum DataType {
    IMAGE = 'Images',
    VIDEO = 'Video',
}

const DataTypeChineseMapping: Record<string, string> = {
    [DataType.IMAGE.toString()]: '图片',
    [DataType.VIDEO.toString()]: '视频',
};

export const GetDataTypeChinese = (type: string): string => DataTypeChineseMapping[type] || '未知';

export enum TaskStatus {
    ANNOTATION = 'annotation',
    VALIDATION = 'validation',
    COMPLETED = 'completed',
}

const TaskStatusChineseMapping: Record<string, string> = {
    [TaskStatus.ANNOTATION.toString()]: '标注',
    [TaskStatus.VALIDATION.toString()]: '验证',
    [TaskStatus.COMPLETED.toString()]: '完成',
};

export const GetTaskStatusChinese = (status: TaskStatus): string => TaskStatusChineseMapping[status] || '未知';

export type ProjectStatus = TaskStatus;

export const GetProjectStatusChinese = (status: ProjectStatus): string => TaskStatusChineseMapping[status] || '未知';

export enum JobStage {
    ANNOTATION = 'annotation',
    VALIDATION = 'validation',
    INSPECTION = 'inspection',
    QUALITYCTL = 'qualityctl',
    ACCEPTANCE = 'acceptance',
}

const JobStageChineseMapping: Record<string, string> = {
    [JobStage.ANNOTATION.toString()]: '标注',
    [JobStage.VALIDATION.toString()]: '审核',
    [JobStage.INSPECTION.toString()]: '质检',
    [JobStage.QUALITYCTL.toString()]: '质控',
    [JobStage.ACCEPTANCE.toString()]: '验收',
};

export const GetJobStageChinese = (stage: string): string => JobStageChineseMapping[stage] || '未知';

export enum JobState {
    NEW = 'new',
    IN_PROGRESS = 'in progress',
    COMPLETED = 'completed',
    REJECTED = 'rejected',
}

const JobStateChineseMapping: Record<string, string> = {
    [JobState.NEW.toString()]: '未开始',
    [JobState.IN_PROGRESS.toString()]: '进行中',
    [JobState.REJECTED.toString()]: '已退回',
    [JobState.COMPLETED.toString()]: '已完成',
};

export const GetJobStateChinese = (state: string): string => JobStateChineseMapping[state] || '未知';

export enum JobType {
    ANNOTATION = 'annotation',
    GROUND_TRUTH = 'ground_truth',
}

export enum DimensionType {
    DIMENSION_2D = '2d',
    DIMENSION_3D = '3d',
}

export enum RQStatus {
    QUEUED = 'queued',
    STARTED = 'started',
    FINISHED = 'finished',
    FAILED = 'failed',
    UNKNOWN = 'unknown',
}

export enum TaskMode {
    ANNOTATION = 'annotation',
    INTERPOLATION = 'interpolation',
}

export enum AttributeType {
    CHECKBOX = 'checkbox',
    RADIO = 'radio',
    SELECT = 'select',
    NUMBER = 'number',
    TEXT = 'text',
}

export enum ObjectType {
    TAG = 'tag',
    SHAPE = 'shape',
    TRACK = 'track',
}

export enum ShapeType {
    RECTANGLE = 'rectangle',
    POLYGON = 'polygon',
    POLYLINE = 'polyline',
    POINTS = 'points',
    ELLIPSE = 'ellipse',
    CUBOID = 'cuboid',
    SKELETON = 'skeleton',
    MASK = 'mask',
}

export enum Source {
    MANUAL = 'manual',
    SEMI_AUTO = 'semi-auto',
    AUTO = 'auto',
    FILE = 'file',
}

export enum LogType {
    loadTool = 'load:cvat',

    loadJob = 'load:job',
    saveJob = 'save:job',
    restoreJob = 'restore:job',
    uploadAnnotations = 'upload:annotations',
    exception = 'send:exception',
    sendTaskInfo = 'send:task_info',

    drawObject = 'draw:object',
    pasteObject = 'paste:object',
    copyObject = 'copy:object',
    propagateObject = 'propagate:object',
    dragObject = 'drag:object',
    resizeObject = 'resize:object',
    deleteObject = 'delete:object',
    lockObject = 'lock:object',
    mergeObjects = 'merge:objects',
    changeAttribute = 'change:attribute',
    changeLabel = 'change:label',

    changeFrame = 'change:frame',
    moveImage = 'move:image',
    zoomImage = 'zoom:image',
    fitImage = 'fit:image',
    rotateImage = 'rotate:image',

    undoAction = 'action:undo',
    redoAction = 'action:redo',

    pressShortcut = 'press:shortcut',
    debugInfo = 'send:debug_info',

    clickElement = 'click:element',

    createConfirmation = 'create:confirmation',
}

export enum HistoryActions {
    CHANGED_LABEL = 'Changed label',
    CHANGED_ATTRIBUTES = 'Changed attributes',
    CHANGED_POINTS = 'Changed points',
    CHANGED_ROTATION = 'Object rotated',
    CHANGED_OUTSIDE = 'Changed outside',
    CHANGED_OCCLUDED = 'Changed occluded',
    CHANGED_ZORDER = 'Changed z-order',
    CHANGED_KEYFRAME = 'Changed keyframe',
    CHANGED_LOCK = 'Changed lock',
    CHANGED_PINNED = 'Changed pinned',
    CHANGED_COLOR = 'Changed color',
    CHANGED_HIDDEN = 'Changed hidden',
    CHANGED_SOURCE = 'Changed source',
    MERGED_OBJECTS = 'Merged objects',
    SPLITTED_TRACK = 'Splitted track',
    GROUPED_OBJECTS = 'Grouped objects',
    CREATED_OBJECTS = 'Created objects',
    REMOVED_OBJECT = 'Removed object',
    REMOVED_FRAME = 'Removed frame',
    RESTORED_FRAME = 'Restored frame',
}

const HistoryActionsChineseMapping: Record<string, string> = {
    [HistoryActions.CHANGED_LABEL.toString()]: '修改标签',
    [HistoryActions.CHANGED_ATTRIBUTES.toString()]: '修改属性',
    [HistoryActions.CHANGED_POINTS.toString()]: '修改关键点',
    [HistoryActions.CHANGED_ROTATION.toString()]: '旋转标注',
    [HistoryActions.CHANGED_OUTSIDE.toString()]: '修改是否在区域内',
    [HistoryActions.CHANGED_OCCLUDED.toString()]: '修改是否遮挡',
    [HistoryActions.CHANGED_ZORDER.toString()]: '修改层叠顺序',
    [HistoryActions.CHANGED_KEYFRAME.toString()]: '修改是否是关键帧',
    [HistoryActions.CHANGED_LOCK.toString()]: '修改是否锁定',
    [HistoryActions.CHANGED_PINNED.toString()]: '修改是否固定',
    [HistoryActions.CHANGED_COLOR.toString()]: '修改颜色',
    [HistoryActions.CHANGED_HIDDEN.toString()]: '修改是否隐藏',
    [HistoryActions.CHANGED_SOURCE.toString()]: '修改来源',
    [HistoryActions.MERGED_OBJECTS.toString()]: '合并标注',
    [HistoryActions.SPLITTED_TRACK.toString()]: '切分追踪',
    [HistoryActions.REMOVED_OBJECT.toString()]: '移除标注',
    [HistoryActions.REMOVED_FRAME.toString()]: '移除帧',
    [HistoryActions.RESTORED_FRAME.toString()]: '恢复帧',
};

export const GetHistoryActionsChinese = (state: string): string => HistoryActionsChineseMapping[state] || '未知';

export enum ModelKind {
    DETECTOR = 'detector',
    INTERACTOR = 'interactor',
    TRACKER = 'tracker',
    CLASSIFIER = 'classifier',
    REID = 'reid',
}

export enum ModelProviders {
    CVAT = 'cvat',
}

export enum ModelReturnType {
    RECTANGLE = 'rectangle',
    TAG = 'tag',
    POLYGON = 'polygon',
    MASK = 'mask',
}

export const colors = [
    '#33ddff',
    '#fa3253',
    '#34d1b7',
    '#ff007c',
    '#ff6037',
    '#ddff33',
    '#24b353',
    '#b83df5',
    '#66ff66',
    '#32b7fa',
    '#ffcc33',
    '#83e070',
    '#fafa37',
    '#5986b3',
    '#8c78f0',
    '#ff6a4d',
    '#f078f0',
    '#2a7dd1',
    '#b25050',
    '#cc3366',
    '#cc9933',
    '#aaf0d1',
    '#ff00cc',
    '#3df53d',
    '#fa32b7',
    '#fa7dbb',
    '#ff355e',
    '#f59331',
    '#3d3df5',
    '#733380',
];

export enum CloudStorageProviderType {
    AWS_S3_BUCKET = 'AWS_S3_BUCKET',
    AZURE_CONTAINER = 'AZURE_CONTAINER',
    GOOGLE_CLOUD_STORAGE = 'GOOGLE_CLOUD_STORAGE',
}

export enum CloudStorageCredentialsType {
    KEY_SECRET_KEY_PAIR = 'KEY_SECRET_KEY_PAIR',
    ACCOUNT_NAME_TOKEN_PAIR = 'ACCOUNT_NAME_TOKEN_PAIR',
    ANONYMOUS_ACCESS = 'ANONYMOUS_ACCESS',
    KEY_FILE_PATH = 'KEY_FILE_PATH',
}

export enum CloudStorageStatus {
    AVAILABLE = 'AVAILABLE',
    NOT_FOUND = 'NOT_FOUND',
    FORBIDDEN = 'FORBIDDEN',
}

export enum MembershipRole {
    WORKER = 'worker',
    SUPERVISOR = 'supervisor',
    MAINTAINER = 'maintainer',
    OWNER = 'owner',
}

const MembershipRoleChineseMapping: Record<string, string> = {
    [MembershipRole.WORKER.toString()]: '普通人员',
    [MembershipRole.SUPERVISOR.toString()]: '普通管理',
    [MembershipRole.MAINTAINER.toString()]: '超级管理',
    [MembershipRole.OWNER.toString()]: '创建者',
};

export const GetMembershipRolesChinese = (role: string): string => MembershipRoleChineseMapping[role] || '未知';

export enum SortingMethod {
    LEXICOGRAPHICAL = 'lexicographical',
    NATURAL = 'natural',
    PREDEFINED = 'predefined',
    RANDOM = 'random',
}

export enum StorageLocation {
    LOCAL = 'local',
    CLOUD_STORAGE = 'cloud_storage',
}

export enum WebhookSourceType {
    ORGANIZATION = 'organization',
    PROJECT = 'project',
}

export enum WebhookContentType {
    JSON = 'application/json',
}

export enum LabelType {
    ANY = 'any',
    RECTANGLE = 'rectangle',
    POLYGON = 'polygon',
    POLYLINE = 'polyline',
    POINTS = 'points',
    ELLIPSE = 'ellipse',
    CUBOID = 'cuboid',
    SKELETON = 'skeleton',
    MASK = 'mask',
    TAG = 'tag',
}

const LabelTypeChineseMapping: Record<string, string> = {
    [LabelType.ANY.toString()]: '任意',
    [LabelType.RECTANGLE.toString()]: '矩形',
    [LabelType.POLYGON.toString()]: '多边形',
    [LabelType.POLYLINE.toString()]: '折线',
    [LabelType.POINTS.toString()]: '关键点',
    [LabelType.ELLIPSE.toString()]: '椭圆',
    [LabelType.CUBOID.toString()]: '立方体',
    [LabelType.SKELETON.toString()]: '骨架',
    [LabelType.MASK.toString()]: '遮盖',
    [LabelType.TAG.toString()]: '标签',
};

export const GetLabelTypeChinese = (labelType: string): string => LabelTypeChineseMapping[labelType] || '未知';

export enum SortingFieldType {
    ID = 'ID',
    NAME = 'Name',
    PROJECT_ID = 'Project ID',
    PROJECT_NAME = 'Project name',
    TASK_ID = 'Task ID',
    TASK_NAME = 'Task name',
    UPDATED_DATE = 'Updated date',
    STAGE = 'Stage',
    STATE = 'State',
    STATUS = 'Status',
    ASSIGNEE = 'Assignee',
    PROVIDER_TYPE = 'Provider type',
    DESCRIPTION = 'Description',
    TYPE = 'Type',
    DISPLAY_NAME = 'Display name',
    RESOURCE = 'Resource',
    CREDENTIAL_TYPE = 'Credential type',
    TARGET_URL = 'Target URL',
    OWNER = 'Owner',
    MODE = 'Mode',
    SUBNET = 'Subnet',
    DIMENSION = 'Dimension',
}

const SortingFieldTypeChineseMapping: Record<string, string> = {
    [SortingFieldType.ID.toString()]: '标识',
    [SortingFieldType.NAME.toString()]: '名称',
    [SortingFieldType.PROJECT_ID.toString()]: '项目标识',
    [SortingFieldType.PROJECT_NAME.toString()]: '项目名称',
    [SortingFieldType.TASK_ID.toString()]: '任务标识',
    [SortingFieldType.TASK_NAME.toString()]: '任务名称',
    [SortingFieldType.UPDATED_DATE.toString()]: '更新时间',
    [SortingFieldType.STAGE.toString()]: '阶段',
    [SortingFieldType.STATE.toString()]: '状态',
    [SortingFieldType.STATUS.toString()]: '状态',
    [SortingFieldType.ASSIGNEE.toString()]: '受托人',
    [SortingFieldType.PROVIDER_TYPE.toString()]: '供应商',
    [SortingFieldType.DESCRIPTION.toString()]: '描述',
    [SortingFieldType.TYPE.toString()]: '类型',
    [SortingFieldType.DISPLAY_NAME.toString()]: '名称',
    [SortingFieldType.RESOURCE.toString()]: '来源',
    [SortingFieldType.CREDENTIAL_TYPE.toString()]: '证书类型',
    [SortingFieldType.TARGET_URL.toString()]: 'URL',
    [SortingFieldType.OWNER.toString()]: '拥有者',
    [SortingFieldType.MODE.toString()]: '模式',
    [SortingFieldType.SUBNET.toString()]: '用途',
    [SortingFieldType.DIMENSION.toString()]: '维度',
};

export const GetSortingFieldTypeChinese = (fieldType: string): string => SortingFieldTypeChineseMapping[fieldType] || '未知';
